import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { ApiError } from '../../../Models/ApiModel';
import { PremiumFeature } from '../../../Models/enums';
import type { ReceiptStatsModel } from '../../../Models/ReceiptModel';
import { ReceiptService } from '../../../Services/ReceiptService';
import { formatMonth } from '../../../Utils/DateUtil';
import { exportCsv, exportJson, exportStamp, type ExportRow } from '../../../Utils/ExportUtil';
import { MoneyText } from '../../SharedArea/MoneyText/MoneyText';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import { ErrorState } from '../../SharedArea/ErrorState/ErrorState';
import { EmptyState } from '../../SharedArea/EmptyState/EmptyState';
import { PremiumGate } from '../Premium/PremiumGate';
import { PremiumSubnav } from '../Premium/PremiumSubnav';
import './ReceiptReports.css';

interface Group { month?: string; name?: string; method?: string; status?: string; count: number; total: number }

interface ReportsPayload {
  byMonth: Group[];
  byStudent: Group[];
  byMethod: Group[];
  byStatus: Group[];
  averageAmount: number;
  sequence: { receiptNumber: string; fiscalYear: number; status: string; issueDate: string | null; amount: string }[];
}

const METHOD_KEYS = {
  cash: 'methodCash',
  bank_transfer: 'methodBankTransfer',
  credit_card: 'methodCreditCard',
  bit: 'methodBit',
  paybox: 'methodPaybox',
  check: 'methodCheck',
  other: 'methodOther'
} as const;

/**
 * What was issued, to whom, how it was paid, and whether the serial run has a
 * hole in it.
 *
 * The gap check is the one thing here that is not a summary. A missing number
 * in a run of official documents is something a tutor may have to explain to
 * an auditor, and finding it by reading a list of two hundred receipts is not
 * a plan.
 */
export function ReceiptReports() {
  return (
    <PremiumGate feature={PremiumFeature.AdvancedReports}>
      <ReceiptReportsInner />
    </PremiumGate>
  );
}

function ReceiptReportsInner() {
  const { tr, language } = useTranslation();
  const [reports, setReports] = useState<ReportsPayload | null>(null);
  const [stats, setStats] = useState<ReceiptStatsModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([ReceiptService.reports(), ReceiptService.stats()])
      .then(([payload, figures]) => {
        setReports(payload as unknown as ReportsPayload);
        setStats(figures);
      })
      .catch(failure => setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR'))
      .finally(() => setLoading(false));
  }, []);

  /**
   * The numbers that were skipped.
   *
   * Only within one fiscal year and one prefix: numbering restarts each year
   * when per-year numbering is on, and a restart is not a gap. The trailing
   * digits are what the sequence actually counts.
   */
  const gaps = useMemo(() => {
    if (!reports) return [];
    const missing: string[] = [];
    const byYear = new Map<number, number[]>();
    for (const entry of reports.sequence) {
      const digits = entry.receiptNumber.match(/(\d+)$/)?.[1];
      if (!digits) continue;
      const list = byYear.get(entry.fiscalYear) ?? [];
      list.push(Number(digits));
      byYear.set(entry.fiscalYear, list);
    }
    for (const [year, numbers] of byYear) {
      numbers.sort((a, b) => a - b);
      for (let index = 1; index < numbers.length; index += 1) {
        for (let value = numbers[index - 1]! + 1; value < numbers[index]!; value += 1) {
          missing.push(`${year}-${value}`);
        }
      }
    }
    return missing;
  }, [reports]);

  function exportRows(): ExportRow[] {
    if (!reports) return [];
    return reports.byMonth.map(group => ({
      [tr('reportMonth')]: group.month ?? '',
      [tr('receiptsCount')]: group.count,
      [tr('total')]: group.total
    }));
  }

  if (loading) return <Spinner />;
  if (error || !reports || !stats) return <ErrorState code={error ?? 'SERVER_ERROR'} />;

  const nothingIssued = reports.byMonth.length === 0;

  return (
    <section className="receipt-reports">
      <PremiumSubnav />
      <h1 className="screen-title">{tr('navReceiptReports')}</h1>

      {nothingIssued
        ? <EmptyState title={tr('noIssuedReceipts')} hint={tr('noIssuedReceiptsHint')} />
        : (
          <>
            <div className="report-figures">
              <Figure label={tr('issuedCount')} value={stats.issuedCount.toLocaleString()} />
              <Figure label={tr('issuedTotal')} value={<MoneyText amount={stats.issuedTotal} />} />
              <Figure label={tr('averageReceipt')} value={<MoneyText amount={Math.round(reports.averageAmount)} />} />
              <Figure label={tr('receiptDraft')} value={stats.draftCount.toLocaleString()} />
              <Figure label={tr('receiptCancelledShort')} value={stats.cancelledCount.toLocaleString()} tone="owed" />
              <Figure label={tr('unreportedTitle')} value={stats.unreportedCount.toLocaleString()} />
            </div>

            {/* A gap is either an explanation waiting to happen or nothing at
                all, so the panel states which and never just disappears. */}
            <section className={gaps.length > 0 ? 'report-gaps has-gaps' : 'report-gaps'}>
              <h2>{tr('sequenceTitle')}</h2>
              {gaps.length === 0
                ? <p>{tr('sequenceComplete')}</p>
                : <p>{tr('sequenceGaps')} <bdi>{gaps.join(', ')}</bdi></p>}
            </section>

            <div className="report-tables">
              <Table
                title={tr('byMonth')}
                column={tr('reportMonth')}
                rows={reports.byMonth.map(group => ({
                  label: group.month ? formatMonth(group.month, language) : '—',
                  count: group.count,
                  total: group.total
                }))}
              />
              <Table
                title={tr('byStudent')}
                column={tr('customerName')}
                rows={reports.byStudent.map(group => ({ label: group.name ?? '—', count: group.count, total: group.total }))}
              />
              <Table
                title={tr('byMethod')}
                column={tr('paymentMethod')}
                rows={reports.byMethod.map(group => ({
                  label: tr(METHOD_KEYS[(group.method ?? 'other') as keyof typeof METHOD_KEYS] ?? 'methodOther'),
                  count: group.count,
                  total: group.total
                }))}
              />
            </div>

            <div className="report-actions">
              <button type="button" className="ghost-button" onClick={() => window.print()}>{tr('printList')}</button>
              <button type="button" className="ghost-button" onClick={() => exportCsv(exportRows(), `receipt-report-${exportStamp()}`)}>CSV</button>
              <button type="button" className="ghost-button" onClick={() => exportJson(reports, `receipt-report-${exportStamp()}`)}>JSON</button>
            </div>
          </>
        )}
    </section>
  );
}

function Figure({ label, value, tone }: { label: string; value: React.ReactNode; tone?: 'owed' }) {
  return (
    <article className={tone ? `report-figure is-${tone}` : 'report-figure'}>
      <strong>{value}</strong>
      <span>{label}</span>
    </article>
  );
}

function Table({ title, column, rows }: {
  title: string;
  /** What the first column holds, which is not the same as what the table is
   *  called: "By month" over a column of months reads as a stutter. */
  column: string;
  rows: { label: string; count: number; total: number }[];
}) {
  const { tr } = useTranslation();
  return (
    <section className="report-table">
      <h2>{title}</h2>
      {/* Its own scroll container. A wide table must never make the page
          itself scroll sideways. */}
      <div className="report-table-scroll">
        <table>
          <thead>
            <tr>
              <th>{column}</th>
              <th>{tr('receiptsCount')}</th>
              <th>{tr('total')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td><bdi>{row.count}</bdi></td>
                <td><MoneyText amount={Math.round(row.total)} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
